'use client';
import { useResource } from '@/lib/use-resource';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Cell,
} from 'recharts';
import type { ScoreReturnAnalysis } from '@/lib/research/score-return';

const fmtPctZero = (v: number) => `${(v * 100).toFixed(1)}%`;

export function ScoreReturnChart() {
  const { data: raw, loading } = useResource<ScoreReturnAnalysis & { totalObservations?: number }>('/api/score-return-curve');
  const data: ScoreReturnAnalysis | null = raw && (raw.totalObservations ?? 0) > 0 ? raw : null;

  if (loading) {
    return <div className="text-[12px] text-[#64748b]">Loading score-return curve…</div>;
  }
  if (!data) {
    return (
      <div className="text-[12px] text-[#64748b] leading-relaxed">
        Score-return curve not yet computed. Run{' '}
        <code className="text-amber-400">npx tsx scripts/compute-score-return-curve.ts</code> to populate.
      </div>
    );
  }

  const universe = data.meanReturnAll;
  const t = data.tiers;

  // Bar height = MEDIAN forward 12m return, not mean. Mean gets murdered by
  // recovery-bouncer outliers in the bottom tail (one stock with +900% drags
  // a small bucket's mean to 500%; the median is stable). Filter buckets
  // with fewer than 30 observations — those are noise.
  const MIN_OBS_FOR_BAR = 30;
  const universeMedian = data.medianReturnAll;
  const barData = data.buckets
    .filter(b => b.count >= MIN_OBS_FOR_BAR)
    .map(b => ({
      label: b.label,
      ret: b.medianReturn,
      mean: b.meanReturn,
      excess: b.medianReturn - universeMedian,
      count: b.count,
      hitRate: b.hitRate,
      sharpe: b.sharpe,
      stdev: b.stdevReturn,
    }));

  // Alpha Zone vs Bottom 5% headline edge — the most actionable comparison
  // for Steve: how much does the model's recommendation tier (top 20/yr) beat
  // the universe's worst 5%? Computed in median (chart-consistent) AND mean.
  const alphaVsBottomMedian = t.alphaZone.medianReturn - t.bottom5pct.medianReturn;
  const alphaVsBottomMean = t.alphaZone.meanReturn - t.bottom5pct.meanReturn;
  const alphaVsBottomSharpe = (t.alphaZone.sharpe ?? 0) - (t.bottom5pct.sharpe ?? 0);

  return (
    <div className="space-y-5">
      {/* Alpha vs Bottom 5% — the model's spread vs the universe tail. */}
      <div className="bg-[#0d1117] border border-amber-500/40 rounded-lg p-4">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold">
            Alpha Zone vs Bottom 5%
          </div>
          <div className="text-[10px] text-[#64748b]">
            {data.years.length}-yr sample · forward 12m return
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[12px]">
          <SpreadCard
            label="Alpha Zone"
            sub={`top 10/yr (n=${t.alphaZone.count})`}
            median={t.alphaZone.medianReturn}
            mean={t.alphaZone.meanReturn}
            sharpe={t.alphaZone.sharpe}
            tone="good"
          />
          <SpreadCard
            label="Bottom 5%"
            sub={`worst 5%/yr (n=${t.bottom5pct.count})`}
            median={t.bottom5pct.medianReturn}
            mean={t.bottom5pct.meanReturn}
            sharpe={t.bottom5pct.sharpe}
            tone="bad"
          />
          <SpreadCard
            label="Edge (Alpha − Bottom)"
            sub="model spread"
            median={alphaVsBottomMedian}
            mean={alphaVsBottomMean}
            sharpe={alphaVsBottomSharpe}
            tone={alphaVsBottomMedian > 0 ? 'good' : 'bad'}
            highlight
          />
        </div>
      </div>

      {/* Headline tier callouts. Card primary = MEDIAN (matches chart),
          mean shown smaller as secondary so Steve can see both. */}
      <div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
          <TierStat
            label="Alpha Zone"
            sub="top 10/yr (~1.3%)"
            median={t.alphaZone.medianReturn}
            mean={t.alphaZone.meanReturn}
            universeMedian={data.medianReturnAll}
            sharpe={t.alphaZone.sharpe}
            count={t.alphaZone.count}
            highlight
          />
          <TierStat
            label="Top 5%"
            sub="~38 stocks/yr"
            median={t.top5pct.medianReturn}
            mean={t.top5pct.meanReturn}
            universeMedian={data.medianReturnAll}
            sharpe={t.top5pct.sharpe}
            count={t.top5pct.count}
          />
          <TierStat
            label="Top 2.5%"
            sub="~+2σ tail"
            median={t.top2sigma.medianReturn}
            mean={t.top2sigma.meanReturn}
            universeMedian={data.medianReturnAll}
            sharpe={t.top2sigma.sharpe}
            count={t.top2sigma.count}
          />
          <TierStat
            label="Universe"
            sub="all stocks"
            median={data.medianReturnAll}
            mean={universe}
            universeMedian={null}
            sharpe={null}
            count={data.totalObservations}
            muted
          />
        </div>

        {/* Bottom-tier callouts so the recovery basket is visible alongside */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px] mt-3">
          <TierStat
            label="Bottom 5%"
            sub="recovery basket"
            median={t.bottom5pct.medianReturn}
            mean={t.bottom5pct.meanReturn}
            universeMedian={data.medianReturnAll}
            sharpe={t.bottom5pct.sharpe}
            count={t.bottom5pct.count}
            warn
          />
          <TierStat
            label="Bottom 2.5%"
            sub="~−2σ tail"
            median={t.bottom2sigma.medianReturn}
            mean={t.bottom2sigma.meanReturn}
            universeMedian={data.medianReturnAll}
            sharpe={t.bottom2sigma.sharpe}
            count={t.bottom2sigma.count}
            warn
          />
          <TierStat
            label="Mid ±1σ"
            sub="middle of pack"
            median={t.midOneSigma.medianReturn}
            mean={t.midOneSigma.meanReturn}
            universeMedian={data.medianReturnAll}
            sharpe={t.midOneSigma.sharpe}
            count={t.midOneSigma.count}
          />
          <div className="bg-[#0d1117] border border-[#1e293b] rounded p-3 flex items-center text-[10px] text-[#94a3b8] leading-snug">
            Median tells the truth. Bottom-tier <strong className="text-white">mean</strong> is inflated by recovery bouncers
            you can&apos;t predict in advance. <strong className="text-white">Median</strong> is what a typical name in the bucket actually delivers.
          </div>
        </div>
      </div>

      {/* Continuous curve */}
      <div>
        <div className="text-[10px] text-[#64748b] uppercase tracking-wide mb-2">
          <strong className="text-[#94a3b8]">Median</strong> forward 12-month return by composite bucket
          · {data.years.length}-year sample ({data.years[0]}–{data.years[data.years.length - 1]})
          · buckets with n &lt; 30 hidden (noise)
        </div>
        <div className="w-full h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 10, right: 12, bottom: 0, left: -8 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#1e293b" />
              <XAxis
                dataKey="label"
                stroke="#64748b"
                fontSize={10}
                tickLine={false}
                interval={0}
              />
              <YAxis
                stroke="#64748b"
                fontSize={10}
                tickLine={false}
                tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
              />
              <ReferenceLine y={universeMedian} stroke="#f59e0b" strokeDasharray="3 3" label={{
                value: `universe median ${fmtPctZero(universeMedian)}`,
                position: 'insideTopRight',
                fill: '#f59e0b',
                fontSize: 10,
              }} />
              <Tooltip
                contentStyle={{ background: '#0d1117', border: '1px solid #1e293b', borderRadius: 4, fontSize: 11 }}
                labelStyle={{ color: '#94a3b8' }}
                formatter={(value, name, ctx) => {
                  if (name === 'ret' && typeof value === 'number') {
                    const p = (ctx as { payload?: { count?: number; hitRate?: number; mean?: number; sharpe?: number } }).payload;
                    const count = p?.count ?? 0;
                    const hit = p?.hitRate ?? 0;
                    const meanVal = p?.mean ?? 0;
                    const sh = p?.sharpe ?? 0;
                    return [
                      `median ${fmtPctZero(value)} (mean ${fmtPctZero(meanVal)}, Sharpe ${sh.toFixed(2)}, ${count} obs, ${(hit * 100).toFixed(0)}% hit)`,
                      'Forward 12m return',
                    ];
                  }
                  return [String(value), String(name)];
                }}
              />
              <Bar dataKey="ret">
                {barData.map((entry, i) => (
                  <Cell key={i} fill={entry.ret > universeMedian ? '#34d399' : '#f87171'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[10px] text-[#475569] mt-2 leading-relaxed">
          Bars show <strong className="text-[#94a3b8]">median</strong> return — robust to recovery-bouncer outliers
          that distort the mean in tail buckets. Universe-median reference line. Green bars beat the median, red bars
          trail. Tooltip shows mean, median, Sharpe, count.
        </p>
      </div>

      <div className="text-[11px] text-[#94a3b8] leading-relaxed border-l border-[#1e293b] pl-3">
        <strong className="text-white">How to read this.</strong> Picking a stock from the Alpha Zone (top 10 by
        composite) historically delivered a forward 12-month return of {fmtPctZero(t.alphaZone.meanReturn)} on average
        — versus {fmtPctZero(universe)} for an arbitrary stock in the universe. Hit rate of{' '}
        {(t.alphaZone.hitRate * 100).toFixed(0)}% means the Alpha Zone produced a positive 12m return in roughly that
        share of stock-years. The relationship is monotonic across the top of the curve: tighter cohort, stronger
        signal. {data.years.length}-year sample,{' '}
        {data.totalObservations.toLocaleString()} stock-years total.
      </div>
    </div>
  );
}

/** Headline card for the Alpha Zone vs Bottom 5% comparison block. */
function SpreadCard({
  label, sub, median, mean, sharpe, tone, highlight = false,
}: {
  label: string;
  sub: string;
  median: number;
  mean: number;
  sharpe: number | null;
  tone: 'good' | 'bad' | 'neutral';
  highlight?: boolean;
}) {
  const toneColor = tone === 'good' ? 'text-emerald-400' : tone === 'bad' ? 'text-red-400' : 'text-[#cbd5e1]';
  const border = highlight ? 'border-amber-500/60' : tone === 'good' ? 'border-emerald-500/30' : tone === 'bad' ? 'border-red-500/30' : 'border-[#1e293b]';
  const sign = (v: number) => (v >= 0 ? '+' : '');
  return (
    <div className={`bg-[#0a0e14] border ${border} rounded p-3`}>
      <div className="text-[10px] uppercase tracking-wide text-[#94a3b8]">{label}</div>
      <div className="text-[9px] text-[#64748b] mb-1">{sub}</div>
      <div className={`text-[22px] font-bold tabular-nums ${toneColor}`}>
        {sign(median)}{(median * 100).toFixed(1)}%
        <span className="text-[9px] text-[#64748b] uppercase tracking-wide ml-1.5 font-medium">median</span>
      </div>
      <div className="text-[10px] text-[#64748b] tabular-nums">
        mean {sign(mean)}{(mean * 100).toFixed(1)}%
        {sharpe != null && Number.isFinite(sharpe) ? <> · Sh {sign(sharpe)}{sharpe.toFixed(2)}</> : null}
      </div>
    </div>
  );
}

function TierStat({
  label, sub, median, mean, universeMedian, sharpe, count, highlight = false, muted = false, warn = false,
}: {
  label: string;
  sub: string;
  median: number;
  mean: number;
  universeMedian: number | null;
  sharpe: number | null;
  count: number;
  highlight?: boolean;
  muted?: boolean;
  warn?: boolean;
}) {
  const valueColor = muted ? 'text-[#94a3b8]' : warn ? 'text-orange-400' : highlight ? 'text-emerald-400' : 'text-white';
  const excess = universeMedian == null ? null : median - universeMedian;
  const excessSign = excess != null && excess >= 0 ? '+' : '';
  const excessColor = excess == null ? '' : excess >= 0 ? 'text-emerald-400' : 'text-red-400';
  const borderColor = warn ? 'border-orange-700/30' : 'border-[#1e293b]';
  return (
    <div className={`bg-[#0d1117] border ${borderColor} rounded p-3`}>
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-[#94a3b8]">{label}</div>
          <div className="text-[9px] text-[#64748b]">{sub}</div>
        </div>
        <div className="text-[9px] text-[#475569] tabular-nums">{count.toLocaleString()} obs</div>
      </div>
      <div className={`text-[20px] font-bold tabular-nums mt-1 ${valueColor}`}>
        {(median * 100).toFixed(1)}%
        <span className="text-[9px] text-[#64748b] uppercase tracking-wide ml-1.5 font-medium">median</span>
      </div>
      <div className="text-[10px] text-[#64748b] tabular-nums">
        mean {(mean * 100).toFixed(1)}%
      </div>
      <div className="flex items-baseline justify-between mt-0.5 text-[10px]">
        {excess != null ? (
          <span className={`tabular-nums font-semibold ${excessColor}`}>
            {excessSign}{(excess * 100).toFixed(1)} pp vs uni
          </span>
        ) : <span className="text-[#475569]">baseline</span>}
        {sharpe != null ? (
          <span className="text-[#64748b] tabular-nums">Sh {sharpe.toFixed(2)}</span>
        ) : null}
      </div>
    </div>
  );
}
